'use client';
import dayjs from 'dayjs';
import * as React from 'react';
import invariant from 'invariant';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import EditIcon from '@mui/icons-material/Edit';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridSingleSelectColDef } from '@mui/x-data-grid';
import CircularProgress from '@mui/material/CircularProgress';

import { formatBoolean } from 'utils/string';

import { useDialogs } from '../useDialogs';
import { CrudContext } from '../shared/context';
import { useActivePage } from '../useActivePage';
import { useNotifications } from '../useNotifications';
import { useLocaleText } from '../AppProvider/LocalizationProvider';
import { PageContainer, type PageContainerProps } from '../PageContainer';

import { DataSourceCache } from './cache';
import { groupFieldsBySections } from './sectioning';
import { useCachedDataSource } from './useCachedDataSource';
import { type CRUDLocaleText, CRUD_DEFAULT_LOCALE_TEXT } from './localeText';
import {
  DataField,
  DataModel,
  DataSource,
  DataModelId,
  DataFieldFormValue,
} from './types';

export interface ShowProps<D extends DataModel> {
  id: DataModelId;
  /**
   * The title of the page.
   */
  pageTitle?: string;
  /**
   * Locale text for the component.
   */
  localeText?: CRUDLocaleText;
  /**
   * Callback fired when the item is successfully deleted.
   */
  onDelete?: (id: DataModelId) => void;
  /**
   * Callback fired when the "Edit" button is clicked.
   */
  onEditClick?: (id: DataModelId) => void;
  /**
   * [Cache](https://mui.com/toolpad/core/react-crud/#data-caching) for the data source.
   */
  dataSourceCache?: DataSourceCache | null;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: {
    pageContainer?: PageContainerProps;
  };
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource?: DataSource<D> & Required<Pick<DataSource<D>, 'getOne'>>;
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: {
    pageContainer?: React.JSXElementConstructor<PageContainerProps>;
  };
}

/**
 *
 * Demos:
 *
 * - [CRUD](https://mui.com/toolpad/core/react-crud/)
 *
 * API:
 *
 * - [Show API](https://mui.com/toolpad/core/api/show)
 */
function Show<D extends DataModel>(props: ShowProps<D>) {
  const {
    id,
    slots,
    onDelete,
    pageTitle,
    slotProps,
    onEditClick,
    dataSourceCache,
    localeText: propsLocaleText,
  } = props;

  const globalLocaleText = useLocaleText();
  const localeText = {
    ...CRUD_DEFAULT_LOCALE_TEXT,
    ...globalLocaleText,
    ...propsLocaleText,
  };

  const crudContext = React.useContext(CrudContext);
  const dataSource = (props.dataSource ??
    crudContext.dataSource) as NonNullable<typeof props.dataSource>;

  invariant(dataSource, 'No data source found.');

  const cache = React.useMemo(() => {
    const manualCache = dataSourceCache ?? crudContext.dataSourceCache;
    return typeof manualCache !== 'undefined'
      ? manualCache
      : new DataSourceCache();
  }, [crudContext.dataSourceCache, dataSourceCache]);
  const cachedDataSource = useCachedDataSource<D>(
    dataSource,
    cache
  ) as NonNullable<typeof props.dataSource>;

  const { fields, validate, ...methods } = cachedDataSource;
  const { getOne, deleteOne } = methods;

  const activePage = useActivePage();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const cachedData = React.useMemo(
    () => cache && (cache.get(JSON.stringify(['getOne', id])) as D),
    [cache, id]
  );

  const [data, setData] = React.useState<D | null>(cachedData);
  const [isLoading, setIsLoading] = React.useState(!cachedData);
  const [error, setError] = React.useState<Error | null>(null);

  const [hasDeleted, setHasDeleted] = React.useState(false);

  const loadData = React.useCallback(async () => {
    setError(null);

    let showData = cachedData;
    if (!showData) {
      setIsLoading(true);

      try {
        showData = await getOne(id);
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }

    if (showData) {
      setData(showData);
    }
    setIsLoading(false);
  }, [cachedData, getOne, id]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleItemEdit = React.useCallback(() => {
    if (onEditClick) {
      onEditClick(id);
    }
  }, [id, onEditClick]);

  const handleItemDelete = React.useCallback(async () => {
    const confirmed = await dialogs.confirm(localeText.deleteConfirmMessage, {
      severity: 'error',
      title: localeText.deleteConfirmTitle,
      okText: localeText.deleteConfirmLabel,
      cancelText: localeText.deleteCancelLabel,
    });

    if (confirmed) {
      setIsLoading(true);
      try {
        await deleteOne?.(id);

        if (onDelete) {
          onDelete(id);
        }

        notifications.show(localeText.deleteSuccessMessage, {
          severity: 'success',
          autoHideDuration: 3000,
        });

        setHasDeleted(true);
      } catch (deleteError) {
        notifications.show(
          `${localeText.deleteErrorMessage} ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          }
        );
      }
      setIsLoading(false);
    }
  }, [
    deleteOne,
    dialogs,
    id,
    localeText.deleteCancelLabel,
    localeText.deleteConfirmLabel,
    localeText.deleteConfirmMessage,
    localeText.deleteConfirmTitle,
    localeText.deleteErrorMessage,
    localeText.deleteSuccessMessage,
    notifications,
    onDelete,
  ]);

  const renderField = React.useCallback(
    (showField: DataField) => {
      if (!data) {
        return '…';
      }

      const { type, field, valueFormatter } = showField;
      const fieldValue = data[field];

      if (valueFormatter) {
        return (
          valueFormatter as (
            value: unknown,
            row: D,
            column: DataField
          ) => string
        )(fieldValue, data, showField);
      }
      if (type === 'boolean') {
        return formatBoolean(Boolean(fieldValue));
      }
      if (type === 'date') {
        return fieldValue
          ? dayjs(fieldValue as string).format('MMMM D, YYYY')
          : '-';
      }
      if (type === 'dateTime') {
        return fieldValue
          ? dayjs(fieldValue as string).format('MMMM D, YYYY h:mm A')
          : '-';
      }
      if (type === 'singleSelect') {
        const { valueOptions, getOptionValue, getOptionLabel } =
          showField as GridSingleSelectColDef;

        if (valueOptions && Array.isArray(valueOptions)) {
          const selectedOption = valueOptions.find((option) => {
            let optionValue: string | number = option as string | number;
            if (typeof option !== 'string' && typeof option !== 'number') {
              optionValue = getOptionValue
                ? getOptionValue(option)
                : option.value;
            }

            return optionValue === fieldValue;
          });

          if (selectedOption) {
            let selectedOptionLabel: string | number = selectedOption as
              string | number;
            if (
              typeof selectedOption !== 'string' &&
              typeof selectedOption !== 'number'
            ) {
              selectedOptionLabel = getOptionLabel
                ? getOptionLabel(selectedOption)
                : selectedOption.label;
            }

            return selectedOptionLabel ? String(selectedOptionLabel) : '-';
          }
        }
      }

      return fieldValue ? String(fieldValue) : '-';
    },
    [data]
  );

  const renderShow = React.useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            m: 1,
            flex: 1,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    if (hasDeleted) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{localeText.deletedItemMessage}</Alert>
        </Box>
      );
    }

    if (!data) {
      return null;
    }

    const visibleFields = fields.filter(
      ({ type }) => type !== 'actions' && type !== 'custom'
    );

    const { sectionedGroups, unsectionedFields } =
      groupFieldsBySections(visibleFields);

    const renderShowItem = (showField: DataField) => {
      const { field, headerName, renderShowField } = showField;

      if (renderShowField) {
        return renderShowField({
          data,
          headerName,
          value: (data?.[field] ?? null) as DataFieldFormValue,
        });
      }

      const renderedField = renderField(showField);

      return (
        <Grid key={field} size={{ sm: 6, xs: 12 }}>
          <Paper sx={{ px: 2, py: 1 }}>
            <Typography variant="overline">{headerName}</Typography>
            <Typography sx={{ mb: 1 }} variant="body1">
              {renderedField &&
              (!Array.isArray(renderedField) || renderedField.length > 0)
                ? renderedField
                : '-'}
            </Typography>
          </Paper>
        </Grid>
      );
    };

    return (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          {unsectionedFields.map(renderShowItem)}
          {sectionedGroups.map((section) => (
            <React.Fragment key={`section:${section.title}`}>
              <Grid size={{ xs: 12 }}>
                <Typography sx={{ mt: 1 }} variant="overline">
                  {section.title}
                </Typography>
              </Grid>
              {section.fields.map(renderShowItem)}
            </React.Fragment>
          ))}
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Stack
          spacing={2}
          direction="row"
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          {onEditClick ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleItemEdit}
            >
              {localeText.editLabel}
            </Button>
          ) : null}
          {deleteOne ? (
            <Button
              color="error"
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={handleItemDelete}
            >
              {localeText.deleteLabel}
            </Button>
          ) : null}
        </Stack>
      </Box>
    );
  }, [
    data,
    deleteOne,
    error,
    fields,
    handleItemDelete,
    handleItemEdit,
    hasDeleted,
    isLoading,
    localeText.deleteLabel,
    localeText.deletedItemMessage,
    localeText.editLabel,
    onEditClick,
    renderField,
  ]);

  const PageContainerSlot = slots?.pageContainer ?? PageContainer;

  return (
    <PageContainerSlot
      title={pageTitle}
      breadcrumbs={
        activePage && pageTitle
          ? [
              ...activePage.breadcrumbs,
              {
                title: pageTitle,
              },
            ]
          : undefined
      }
      {...slotProps?.pageContainer}
    >
      <Box sx={{ flex: 1, width: '100%', display: 'flex' }}>{renderShow}</Box>
    </PageContainerSlot>
  );
}

Show.propTypes /* remove-proptypes */ = {
  /**
   * Callback fired when the item is successfully deleted.
   */
  onDelete: PropTypes.func,
  /**
   * Callback fired when the "Edit" button is clicked.
   */
  onEditClick: PropTypes.func,
  /**
   * The title of the page.
   */
  pageTitle: PropTypes.string,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource: PropTypes.object,
  /**
   * Locale text for the component.
   */
  localeText: PropTypes.object,
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.shape({
    pageContainer: PropTypes.object,
  }),
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots: PropTypes.shape({
    pageContainer: PropTypes.elementType,
  }),
  /**
   * @ignore
   */
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /**
   * [Cache](https://mui.com/toolpad/core/react-crud/#data-caching) for the data source.
   */
  dataSourceCache: PropTypes.shape({
    get: PropTypes.func.isRequired,
    set: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
    ttl: PropTypes.number.isRequired,
    cache: PropTypes.object.isRequired,
  }),
} as any;

export { Show };
